import React, { useRef } from 'react';
import { getContext, useCombinedRefs, isAnimationDisabled } from 'common';
import _merge from 'lodash/merge';
import Styled from './styles';

export interface CardCoreProps extends Omit<React.ComponentPropsWithRef<'div'>, 'onSelect'> {
  /** Optional prop, however this prop is utilized when using a Card in CardCollection component. */
  id?: string;
  /** Content is placed here. */
  children: React.ReactNode;
  /** Toggles selection visuals. */
  selected?: boolean;
  /** onSelect callback - triggered when card is selected (used in conjunction with 'selected' prop). Returns card id. */
  onSelect?: (arg0: React.ChangeEvent, arg1: string) => void;
  /** Toggles expansions visuals. */
  expanded?: boolean;
  /** Toggle hover effect */
  hoverEffect?: boolean;
  /** Toggle focus effect */
  focusEffect?: boolean;
  /** Toggle between visual variants of Card.
   *  'stack' -  shows multiple cards stacked together (for use when indicating that there are multiple cards under this card)
   *  'modal' -  toggles modal styles
   */
  variant?: 'default' | 'stack' | 'modal';
  /** If `true` renders focused state. */
  focused?: boolean;
  /** If `true` shows checkbox indicating card is selectable. */
  $showSelectableCards?: boolean;
  /** If `true` renders card collection. */
  $cardCollection?: boolean;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** @ignore */
  style?: React.CSSProperties;
}

/** Card is a rectangular sheet of content that provides context and an entry into more in-depth information or view. */
const CardCore = React.forwardRef<HTMLElement, CardCoreProps>((props: CardCoreProps, ref) =>
  getContext(({ RTL: rtl, theme: globalTheme, disableAnimation }) => {
    const containerRef = useRef(null);
    const combinedRef = useCombinedRefs(ref, containerRef);
    const {
      id, children, style, expanded, focused, selected, variant, theme, onSelect, disableAnimation: propsdisableAnimation, ...otherProps
    } = props;
    const resolvedTheme = _merge({}, globalTheme.CARD, theme);
    return (
      <Styled.CardContainer
        id={id}
        $rtl={rtl}
        expanded={expanded}
        ref={combinedRef}
        tabIndex="0"
        role="link"
        focused={focused}
        selected={selected}
        onSelect={onSelect}
        style={style}
        $modal={variant === 'modal'}
        $theme={resolvedTheme}
        $disableAnimation={isAnimationDisabled(propsdisableAnimation, disableAnimation)}
        {...otherProps}
      >
        {children}
        {variant === 'stack' && (
          <>
            <Styled.CardStackOne $theme={resolvedTheme} />
            <Styled.CardStackTwo $theme={resolvedTheme} />
          </>
        )}
      </Styled.CardContainer>
    );
  }));
CardCore.displayName = 'CardCore';

CardCore.defaultProps = {
  hoverEffect: true,
  focusEffect: true,
  id: undefined,
  onSelect: undefined,
  selected: false,
  expanded: false,
  variant: 'default',
  focused: false,
  $showSelectableCards: false,
  $cardCollection: false,
  disableAnimation: undefined,
  theme: {},
  style: {}
};

export default CardCore;
